import { Injectable } from '@angular/core';
import { 
  Offer, 
  OfferStatus, 
  ReservationAdminStatus,
  ReservationClientStatus,
  ReservationEventType,
  OfferWithActions 
} from '../_models/reservation.model';

@Injectable({
  providedIn: 'root'
})
export class OfferUtilsService {
  
  // French translations for reservation statuses
  private readonly frenchTranslations = {
    clientStatus: {
      PENDING: 'En attente',
      ACCEPTED: 'Accepté',
      REJECTED_BY_CLIENT: 'Rejeté par le client'
    },
    
    adminStatus: {
      WAITING_OFFER: 'En attente d\'offre',
      OFFER_CREATED: 'Offre créée',
      CONFIRMED: 'Accepté',
      REJECTED_BY_ADMIN: 'Rejeté par l\'administrateur',
      CANCELLED: 'Annulé'
    },
    
    eventTypes: {
      SECURITY_GUARDING: 'Garde de Sécurité',
      EVENT_SECURITY: 'Sécurité Événementielle',
      PERSONAL_PROTECTION: 'Protection Personnelle',
      SURVEILLANCE: 'Surveillance',
      SECURITY_AUDIT: 'Audit de Sécurité',
      TRANSPORT_SECURITY: 'Sécurité de Transport',
      OTHER: 'Autre'
    }
  };

  constructor() { }

  /**
   * Enriches an Offer object with action permissions and computed properties
   */
  enrichOfferWithActions(offer: Offer): OfferWithActions {
    const canEdit = this.canEditOffer(offer);
    const canDelete = this.canDeleteOffer(offer);
    const canAddAgents = this.canManageAgents(offer);
    
    const assignedAgentNames = this.getAssignedAgentNames(offer);
    const assignedAgentCount = offer.agents?.length || 0;

    return {
      ...offer,
      canEdit,
      canDelete,
      canAddAgents,
      assignedAgentNames,
      assignedAgentCount,
      formattedPrice: this.formatPrice(offer.price),
      formattedDate: this.formatOfferDate(offer.createdAt)
    };
  }

  /**
   * Check if an offer can be edited
   */
  canEditOffer(offer: Offer): boolean {
    return offer.status === OfferStatus.PENDING;
  }

  /**
   * Check if an offer can be deleted
   */
  canDeleteOffer(offer: Offer): boolean {
    return offer.status === OfferStatus.PENDING;
  }

  /**
   * Check if agents can be managed for an offer
   */
  canManageAgents(offer: Offer): boolean {
    return offer.status === OfferStatus.PENDING;
  }

  /**
   * Check if a new offer can be created for a reservation
   */
  canCreateOfferForReservation(adminStatus: ReservationAdminStatus): boolean {
    return adminStatus === ReservationAdminStatus.WAITING_OFFER || 
           adminStatus === ReservationAdminStatus.OFFER_CREATED;
  }

  /**
   * Get formatted agent names for display
   */
  getAssignedAgentNames(offer: Offer): string {
    if (!offer.agents || offer.agents.length === 0) {
      return 'Aucun agent assigné';
    }
    return offer.agents.map(agent => `${agent.firstName} ${agent.lastName}`).join(', ');
  }

  /**
   * Get CSS class for offer status badge
   */
  getOfferStatusClass(status: OfferStatus): string {
    switch (status) {
      case OfferStatus.PENDING:
        return 'status-pending';
      case OfferStatus.ACCEPTED:
        return 'status-accepted';
      case OfferStatus.REJECTED:
        return 'status-rejected';
      case OfferStatus.CANCELLED:
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

  /**
   * Get localized status text
   */
  getOfferStatusText(status: OfferStatus): string {
    switch (status) {
      case OfferStatus.PENDING:
        return 'En attente';
      case OfferStatus.ACCEPTED:
        return 'Accepté';
      case OfferStatus.REJECTED:
        return 'Rejeté';
      case OfferStatus.CANCELLED:
        return 'Annulé';
      default:
        return 'Inconnu';
    }
  }

  /**
   * Format price for display
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price);
  }

  /**
   * Format offer date for display
   */
  formatOfferDate(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get all offers with actions from a reservation
   */
  getOffersWithActions(offers: Offer[] | undefined): OfferWithActions[] {
    if (!offers || offers.length === 0) {
      return [];
    }
    
    return offers.map(offer => this.enrichOfferWithActions(offer));
  }

  /**
   * Sort offers by status (PENDING first, then by creation date)
   */
  sortOffersByStatus(offers: OfferWithActions[]): OfferWithActions[] {
    return [...offers].sort((a, b) => {
      // PENDING offers first
      if (a.status === OfferStatus.PENDING && b.status !== OfferStatus.PENDING) return -1;
      if (b.status === OfferStatus.PENDING && a.status !== OfferStatus.PENDING) return 1;
      
      // Then sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  /**
   * Filter offers by status
   */
  filterOffersByStatus(offers: OfferWithActions[], status: OfferStatus): OfferWithActions[] {
    return offers.filter(offer => offer.status === status);
  }

  /**
   * Get offer statistics
   */
  getOfferStatistics(offers: OfferWithActions[]): {
    total: number;
    pending: number;
    accepted: number;
    rejected: number;
    cancelled: number;
  } {
    return {
      total: offers.length,
      pending: offers.filter(o => o.status === OfferStatus.PENDING).length,
      accepted: offers.filter(o => o.status === OfferStatus.ACCEPTED).length,
      rejected: offers.filter(o => o.status === OfferStatus.REJECTED).length,
      cancelled: offers.filter(o => o.status === OfferStatus.CANCELLED).length
    };
  }

  /**
   * Get CSS class for reservation client status badge
   */
  getReservationClientStatusClass(status: ReservationClientStatus): string {
    switch (status) {
      case ReservationClientStatus.PENDING:
        return 'status-pending';
      case ReservationClientStatus.ACCEPTED:
        return 'status-accepted';
      case ReservationClientStatus.REJECTED_BY_CLIENT:
        return 'status-rejected';
      default:
        return 'status-default';
    }
  }

  /**
   * Get localized text for reservation client status
   */
  getReservationClientStatusText(status: ReservationClientStatus): string {
    return this.frenchTranslations.clientStatus[status] || status;
  }

  /**
   * Get CSS class for reservation admin status badge
   */
  getReservationAdminStatusClass(status: ReservationAdminStatus): string {
    switch (status) {
      case ReservationAdminStatus.WAITING_OFFER:
        return 'badge-info';
      case ReservationAdminStatus.OFFER_CREATED:
        return 'badge-primary';     
      case ReservationAdminStatus.CONFIRMED:
        return 'badge-success';
      case ReservationAdminStatus.REJECTED_BY_ADMIN:
        return 'badge-danger';
      case ReservationAdminStatus.CANCELLED:
        return 'badge-secondary';
      default:
        return 'badge-light';
    }
  }

  /**
   * Get localized text for reservation admin status
   */
  getReservationAdminStatusText(status: ReservationAdminStatus): string {
    return this.frenchTranslations.adminStatus[status] || status;
  }

  /**
   * Get localized text for event type
   */
  getReservationEventTypeText(eventType: ReservationEventType): string {
    return this.frenchTranslations.eventTypes[eventType] || eventType;
  }

  /**
   * Get overall reservation status class (combines client and admin status)
   */
  getReservationOverallStatusClass(
    clientStatus: ReservationClientStatus, 
    adminStatus: ReservationAdminStatus
  ): string {
    // Priority: Accepted > Rejected > Under Review > Waiting
    if (clientStatus === ReservationClientStatus.ACCEPTED && adminStatus === ReservationAdminStatus.CONFIRMED) {
      return 'badge-success'; // Fully accepted
    }
    
    if (clientStatus === ReservationClientStatus.REJECTED_BY_CLIENT || adminStatus === ReservationAdminStatus.REJECTED_BY_ADMIN) {
      return 'badge-danger'; // Rejected by either party
    }
    
    if (adminStatus === ReservationAdminStatus.WAITING_OFFER) {
      return 'badge-info'; // Waiting for offer
    }
    
    if (adminStatus === ReservationAdminStatus.OFFER_CREATED) {
      return 'badge-primary'; // Offer created
    }
    
    if (adminStatus === ReservationAdminStatus.CANCELLED) {
      return 'badge-secondary'; // Cancelled
    }
    
    return 'badge-light'; // Default
  }

  /**
   * Get overall reservation status text (combines client and admin status)
   */
  getReservationOverallStatusText(
    clientStatus: ReservationClientStatus, 
    adminStatus: ReservationAdminStatus
  ): string {
    // Priority: Accepted > Rejected > Under Review > Waiting
    if (clientStatus === ReservationClientStatus.ACCEPTED && adminStatus === ReservationAdminStatus.CONFIRMED) {
      return 'Accepté'; // Fully accepted
    }
    
    if (clientStatus === ReservationClientStatus.REJECTED_BY_CLIENT) {
      return 'Rejeté par le client';
    }
    
    if (adminStatus === ReservationAdminStatus.REJECTED_BY_ADMIN) {
      return 'Rejeté par l\'administrateur';
    }
    
    if (adminStatus === ReservationAdminStatus.WAITING_OFFER) {
      return 'En attente d\'offre';
    }
    
    if (adminStatus === ReservationAdminStatus.OFFER_CREATED) {
      return 'Offre créée';
    }
    
    if (adminStatus === ReservationAdminStatus.CANCELLED) {
      return 'Annulé';
    }
    
    return this.getReservationAdminStatusText(adminStatus);
  }

  /**
   * Check if reservation can be edited by client
   */
  canClientEditReservation(
    clientStatus: ReservationClientStatus, 
    adminStatus: ReservationAdminStatus
  ): boolean {
    return clientStatus === ReservationClientStatus.PENDING && 
           adminStatus !== ReservationAdminStatus.CONFIRMED &&
           adminStatus !== ReservationAdminStatus.REJECTED_BY_ADMIN &&
           adminStatus !== ReservationAdminStatus.CANCELLED;
  }

  /**
   * Check if reservation can be cancelled by client
   */
  canClientCancelReservation(
    clientStatus: ReservationClientStatus, 
    adminStatus: ReservationAdminStatus
  ): boolean {
    return clientStatus === ReservationClientStatus.PENDING && 
           adminStatus !== ReservationAdminStatus.CONFIRMED &&
           adminStatus !== ReservationAdminStatus.REJECTED_BY_ADMIN &&
           adminStatus !== ReservationAdminStatus.CANCELLED;
  }

  /**
   * Check if reservation can accept offers
   */
  canReservationAcceptOffers(adminStatus: ReservationAdminStatus): boolean {
    return adminStatus === ReservationAdminStatus.WAITING_OFFER || 
           adminStatus === ReservationAdminStatus.OFFER_CREATED;
  }

  /**
   * Check if reservation can be refused by admin
   */
  canAdminRefuseReservation(adminStatus: ReservationAdminStatus): boolean {
    return adminStatus !== ReservationAdminStatus.REJECTED_BY_ADMIN && 
           adminStatus !== ReservationAdminStatus.CANCELLED;
  }

  /**
   * is Reservation Fully Accepted by Client and
   */
  canShowCreateOfferButton(adminStatus: ReservationAdminStatus): boolean {
    return adminStatus === ReservationAdminStatus.WAITING_OFFER;
  }

  /**
   * Format reservation date for display
   */
  formatReservationDate(dateString: string, includeTime: boolean = true): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    if (includeTime) {
      return date.toLocaleDateString('fr-BE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      return date.toLocaleDateString('fr-BE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }

  /**
   * Format reservation duration (start to end)
   */
  formatReservationDuration(startDateTime: string, endDateTime: string): string {
    if (!startDateTime || !endDateTime) return '';
    
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}min`;
    }
  }

  /**
   * Get reservation priority based on status and date
   */
  getReservationPriority(
    clientStatus: ReservationClientStatus, 
    adminStatus: ReservationAdminStatus,
    startDateTime: string
  ): number {
    const now = new Date();
    const startDate = new Date(startDateTime);
    const daysUntil = Math.floor((startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    let priority = 0;
    
    // Status priority
    if (adminStatus === ReservationAdminStatus.WAITING_OFFER) priority += 100;
    if (adminStatus === ReservationAdminStatus.OFFER_CREATED) priority += 80;
    if (clientStatus === ReservationClientStatus.ACCEPTED) priority += 40;
    
    // Urgency priority (closer dates get higher priority)
    if (daysUntil <= 7) priority += 50; // Within a week
    if (daysUntil <= 3) priority += 30; // Within 3 days
    if (daysUntil <= 1) priority += 20; // Within 1 day
    
    return priority;
  }
}